// Coastline terrain for the shore-bombardment missions. The land is an
// analytic heightfield — heightAt(x, z) is the single source of truth used
// by the render mesh, shell impacts, target placement, and the PPI landmass
// paint, so a 16-inch round always bursts exactly on the hillside you see.
//
// The coast runs parallel to world Z. `dir` puts the land on +X (Lebanon:
// the ship works west of Beirut) or -X (Vietnam: the gunline steams in the
// Tonkin Gulf with the DMZ coast to the west).

import * as THREE from "three";

// deterministic layout PRNG so the same mission always builds the same coast
function lcg(seed) {
  let s = seed >>> 0;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
}

const PROFILES = {
  // I Corps coast off the DMZ: beach berm, paddy plain, jungle foothills,
  // with the Cua Viet river mouth cutting the plain.
  vietnam(d, s) {
    let h = 0.5 + Math.min(4, d * 0.03);
    h += Math.max(0, Math.min(1, (d - 300) / 3000)) * 14;
    h += Math.min(Math.max(0, (d - 3500) / 1000) * 26, 240);
    h += (Math.sin(d * 0.0011 + Math.sin(s * 0.0007) * 2.1)
        + Math.sin(s * 0.0009 + d * 0.0004)) * Math.min(30, 4 + d * 0.004);
    const riv = Math.abs(s + 4200);
    if (riv < 900) h = Math.min(h, riv * riv * 0.00002 + 0.3);
    return Math.max(0.2, h);
  },
  // Wonsan: a working harbor rim, then the bare Kalma ridges climbing fast —
  // the gun caves looked straight down on the fleet's anchorage. A rail
  // valley threads north along the coastal plain.
  korea(d, s) {
    let h = 0.4 + Math.min(3, d * 0.04);
    h += Math.max(0, Math.min(1, (d - 250) / 1500)) * 30;
    h += Math.min(Math.max(0, (d - 1800) / 900) * 40, 430);
    h += (Math.sin(d * 0.0016 + Math.sin(s * 0.0009) * 2.4)
        + Math.sin(s * 0.0013 - d * 0.0005)) * Math.min(60, 6 + d * 0.008);
    const valley = Math.abs(s - 3400);
    if (valley < 800 && d > 700) h = Math.min(h, 16 + valley * 0.025);
    return Math.max(0.2, Math.min(760, h));
  },
  // Beirut and the Shouf: a narrow urban shelf, then the mountains stack up
  // fast — snow on the high ridge in February.
  lebanon(d, s) {
    let h = 0.5 + Math.min(3, d * 0.05) + Math.max(0, d - 150) * 0.008;
    if (d > 2600) h += Math.pow((d - 2600) / 1000, 1.35) * 75;
    h += (Math.sin(d * 0.0013 + s * 0.0005) + Math.sin(s * 0.0011 - d * 0.0003))
       * Math.min(45, d * 0.004);
    return Math.max(0.2, Math.min(980, h));
  },
};

const PALETTES = {
  vietnam(h, city) {
    if (h < 4) return [0.79, 0.72, 0.55];        // sand
    if (h < 22) return [0.42, 0.55, 0.33];       // paddy green
    if (h < 140) return [0.30, 0.44, 0.26];      // jungle
    return [0.26, 0.36, 0.24];                   // high canopy
  },
  korea(h, city) {
    if (h < 4) return [0.76, 0.70, 0.56];        // harbor sand
    if (h < 45) return [0.52, 0.50, 0.36];       // dun coastal scrub, spring 1951
    if (h < 260) return [0.45, 0.42, 0.31];      // brown hills
    return [0.50, 0.48, 0.43];                   // bare rock ridges
  },
  lebanon(h, city) {
    if (city) return [0.68, 0.66, 0.62];         // urban shelf
    if (h < 4) return [0.80, 0.74, 0.58];        // sand
    if (h < 130) return [0.55, 0.55, 0.40];      // scrub
    if (h < 550) return [0.52, 0.48, 0.40];      // bare slopes
    if (h < 800) return [0.58, 0.55, 0.50];      // rock
    return [0.86, 0.88, 0.90];                   // February snow line
  },
};

export function makeTerrain(scene, kind, opts = {}) {
  const dir = opts.dir ?? 1;          // +1: land toward +X, -1: land toward -X
  const coastX = opts.coastX ?? 6500; // unsigned distance of the surf line from origin
  const profile = PROFILES[kind];
  const palette = PALETTES[kind];
  const city = kind === "lebanon"
    ? (d, s) => d > 120 && d < 2600 && Math.abs(s) < 5200
    : () => false;

  const heightAt = (x, z) => {
    const d = (x * dir) - coastX;     // meters inland
    if (d <= 0) return 0;
    return profile(d, z);
  };
  const cityAt = (x, z) => city((x * dir) - coastX, z);

  // ---- render mesh --------------------------------------------------------
  const S0 = -27000, S1 = 27000, D0 = -1600, D1 = 23000, STEP = 250;
  const nd = Math.floor((D1 - D0) / STEP) + 1;
  const ns = Math.floor((S1 - S0) / STEP) + 1;
  const pos = new Float32Array(nd * ns * 3);
  const col = new Float32Array(nd * ns * 3);
  const sunDir = new THREE.Vector3(-2600, 1500, 900).normalize();
  let p = 0;
  for (let i = 0; i < nd; i++) {
    const d = D0 + i * STEP;
    for (let j = 0; j < ns; j++) {
      const s = S0 + j * STEP;
      const h = d <= 0 ? -9 + d * 0.004 : profile(d, s);
      const x = (coastX + d) * dir;
      pos[p * 3] = x; pos[p * 3 + 1] = h; pos[p * 3 + 2] = s;
      // slope-lit vertex color
      const hd = (d <= 0 ? -9 : profile(d + STEP, s)) - h;
      const hs = (d <= 0 ? -9 : profile(d, s + STEP)) - h;
      const n = new THREE.Vector3(-hd * dir, STEP, -hs).normalize();
      const lit = 0.62 + Math.max(0, n.dot(sunDir)) * 0.5;
      const c = d <= 0 ? [0.24, 0.38, 0.45] : palette(h, city(d, s));
      col[p * 3] = c[0] * lit; col[p * 3 + 1] = c[1] * lit; col[p * 3 + 2] = c[2] * lit;
      p++;
    }
  }
  const idx = [];
  for (let i = 0; i < nd - 1; i++)
    for (let j = 0; j < ns - 1; j++) {
      const a = i * ns + j, b = a + ns;
      // keep winding so faces point up regardless of which side the land is on
      if (dir > 0) idx.push(a, a + 1, b, a + 1, b + 1, b);
      else idx.push(a, b, a + 1, a + 1, b, b + 1);
    }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
  geo.setIndex(idx);
  const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ vertexColors: true }));
  mesh.name = "terrain";

  const group = new THREE.Group();
  group.add(mesh);

  // ---- Beirut skyline: instanced blocks on the urban shelf -----------------
  if (kind === "lebanon") {
    const rnd = lcg(19840208);
    const N = 340;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const inst = new THREE.InstancedMesh(box, new THREE.MeshBasicMaterial(), N);
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), tv = new THREE.Vector3();
    const cc = new THREE.Color();
    for (let i = 0; i < N; i++) {
      const d = 200 + rnd() * 2300;
      const s = (rnd() * 2 - 1) * 5000;
      const h = 14 + rnd() * rnd() * 52;
      const base = profile(d, s);
      tv.set((coastX + d) * dir, base + h / 2 - 1, s);
      sc.set(24 + rnd() * 46, h, 24 + rnd() * 46);
      m.compose(tv, q, sc);
      inst.setMatrixAt(i, m);
      const g = 0.52 + rnd() * 0.22;
      inst.setColorAt(i, cc.setRGB(g, g * 0.99, g * 0.95));
    }
    inst.instanceMatrix.needsUpdate = true;
    if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
    group.add(inst);
  }

  scene.add(group);
  return { group, heightAt, cityAt, kind, dir, coastX };
}
